import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import {
  PackageOpen,
  Printer,
  ListChecks,
  Truck,
  CheckCircle2,
  Trash2,
  X,
  PackageCheck,
} from "lucide-react";
import {
  useFulfillmentCart,
  useRemoveFromCart,
  useClearCart,
  useBulkSetStatus,
  useBulkSetTracking,
} from "@/hooks/use-fulfillment-cart";
import {
  buildPickList,
  generatePackingSlipsPDF,
  CARRIERS,
  type CartItemView,
} from "@/lib/fulfillment-cart";
import { useCurrency } from "@/lib/currency";

export const Route = createFileRoute("/_authenticated/fulfillment-cart")({
  head: () => ({ meta: [{ title: "Fulfillment Cart — Want To Buy" }] }),
  component: FulfillmentCartPage,
});

type TrackingDraft = Record<string, { carrier: string; tracking_number: string }>;

function FulfillmentCartPage() {
  const { data, isLoading } = useFulfillmentCart();
  const { display, format } = useCurrency();
  const fmt = (usd: number) => `USD $${usd.toFixed(2)}${display !== "USD" ? ` · ≈ ${format(usd)}` : ""}`;
  const items = (data?.items ?? []) as CartItemView[];
  const remove = useRemoveFromCart();
  const clear = useClearCart();
  const bulkStatus = useBulkSetStatus();
  const bulkTracking = useBulkSetTracking();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [packed, setPacked] = useState<Set<string>>(new Set());
  const [tracking, setTracking] = useState<TrackingDraft>({});
  const [pickOpen, setPickOpen] = useState(false);
  const [confirm, setConfirm] = useState<null | "mark_shipped" | "save_tracking">(null);

  const allIds = useMemo(() => items.map((i) => i.transaction.id), [items]);
  const selectedItems = items.filter((i) => selected.has(i.transaction.id));
  const allSelected = items.length > 0 && selected.size === items.length;

  const totals = useMemo(() => {
    const src = selectedItems.length ? selectedItems : items;
    let revenue = 0;
    let shipping = 0;
    let unitCount = 0;
    for (const i of src) {
      revenue += i.transaction.amount_cents;
      shipping += i.transaction.shipping_cents ?? 0;
      unitCount += i.order?.quantity ?? 1;
    }
    return { revenue, shipping, unitCount, orders: src.length };
  }, [selectedItems, items]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(allIds));

  const togglePacked = (id: string) =>
    setPacked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const sellerLabel = "WantToBuy.ca Seller";

  const printSlips = () => {
    const src = selectedItems.length ? selectedItems : items;
    if (!src.length) return;
    generatePackingSlipsPDF(src, sellerLabel);
  };

  const onConfirm = async () => {
    const ids = selectedItems.map((i) => i.transaction.id);
    if (confirm === "mark_shipped") {
      await bulkStatus.mutateAsync({ transaction_ids: ids, action: "mark_shipped" });
    } else if (confirm === "save_tracking") {
      const entries = ids
        .map((id) => ({ transaction_id: id, ...tracking[id] }))
        .filter((e) => e.carrier && e.tracking_number && e.tracking_number.length >= 3) as any;
      if (entries.length) await bulkTracking.mutateAsync(entries);
    }
    setConfirm(null);
    setSelected(new Set());
  };


  return (
    <AppShell>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <PackageOpen className="h-7 w-7 text-primary" /> Fulfillment Cart
            <span className="text-base font-normal text-muted-foreground">({items.length} {items.length === 1 ? "order" : "orders"})</span>
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Process multiple paid orders together — print slips, enter tracking, and bulk-fulfill.
          </p>
        </div>
        {items.length > 0 && (
          <button
            onClick={() => {
              if (confirm) return;
              if (window.confirm("Remove all orders from the fulfillment cart?")) clear.mutate();
            }}
            className="text-xs font-semibold text-muted-foreground hover:text-destructive"
          >
            Clear cart
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="panel p-10 text-center text-sm text-muted-foreground">Loading cart…</div>
      ) : items.length === 0 ? (
        <div className="panel p-10 text-center">
          <PackageOpen className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <div className="font-semibold">Your fulfillment cart is empty</div>
          <p className="mt-1 text-sm text-muted-foreground">
            Open a sold order and click <em>Add to Fulfillment Cart</em> to start.
          </p>
          <Link to="/sold-items" className="mt-4 inline-block btn-primary px-4 py-2 text-sm">
            Go to Sold Items
          </Link>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          {/* LEFT: orders */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 px-1">
              <label className="inline-flex items-center gap-2 text-xs font-semibold">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4 rounded" />
                Select all
              </label>
              <div className="text-xs text-muted-foreground">{selected.size} selected</div>
            </div>

            {items.map((it) => {
              const id = it.transaction.id;
              const isSel = selected.has(id);
              const isPacked = packed.has(id);
              const shortId = id.slice(0, 8).toUpperCase();
              return (
                <div key={id} className={`panel p-4 transition ${isSel ? "ring-1 ring-primary/50" : ""}`}>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggle(id)}
                      className="mt-1 h-4 w-4 rounded"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-display text-base font-bold">
                            Order #{shortId}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(it.transaction.created_at).toLocaleDateString()} ·{" "}
                            <Link
                              to="/profile/$username"
                              params={{ username: it.buyer?.username ?? "" }}
                              className="hover:text-primary"
                            >
                              @{it.buyer?.username ?? "buyer"}
                            </Link>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusPill status={it.transaction.status} />
                          {isPacked && (
                            <span className="rounded-full border border-success/40 bg-success/15 px-2 py-0.5 text-[11px] font-semibold text-success">
                              Packed
                            </span>
                          )}
                          <button
                            onClick={() => togglePacked(id)}
                            className="rounded-md border border-border px-2 py-1 text-[11px] font-semibold hover:bg-muted"
                            title="Mark as packed (local checkpoint)"
                          >
                            <PackageCheck className="inline h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => remove.mutate(id)}
                            className="rounded-md border border-border px-2 py-1 text-[11px] font-semibold text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                            title="Remove from cart"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>

                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Item</div>
                          <div className="text-sm font-semibold">
                            {it.order?.card_name ?? "—"}
                            {it.order?.quantity && it.order.quantity > 1 ? ` × ${it.order.quantity}` : ""}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {it.order?.tcg ?? "—"}
                            {it.order?.condition_min ? ` · min ${it.order.condition_min}` : ""}
                          </div>
                          <div className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground">Total</div>
                          <div className="font-mono text-sm font-semibold">
                            {it.transaction.currency} {(it.transaction.amount_cents / 100).toFixed(2)}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Ship to</div>
                          <div className="text-sm">{it.transaction.ship_to_name ?? "—"}</div>
                          <Addr addr={it.transaction.ship_to_address} />
                        </div>
                      </div>

                      <div className="mt-3 grid gap-2 sm:grid-cols-[140px_1fr]">
                        <select
                          value={tracking[id]?.carrier ?? it.shipment?.carrier_name ?? it.shipment?.carrier ?? ""}
                          onChange={(e) =>
                            setTracking((t) => ({
                              ...t,
                              [id]: { carrier: e.target.value, tracking_number: t[id]?.tracking_number ?? "" },
                            }))
                          }
                          disabled={!!it.shipment}
                          className="h-9 rounded-md border border-border bg-panel px-2 text-xs disabled:opacity-60"
                        >
                          <option value="">Carrier…</option>
                          {CARRIERS.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                        <input
                          value={tracking[id]?.tracking_number ?? it.shipment?.tracking_number ?? ""}
                          onChange={(e) =>
                            setTracking((t) => ({
                              ...t,
                              [id]: { carrier: t[id]?.carrier ?? "", tracking_number: e.target.value },
                            }))
                          }
                          disabled={!!it.shipment}
                          placeholder="Tracking number"
                          className="h-9 rounded-md border border-border bg-panel px-3 text-xs disabled:opacity-60"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* RIGHT: summary */}
          <aside className="space-y-4 lg:sticky lg:top-28 self-start">
            <div className="panel p-5">
              <div className="font-display text-base font-bold">Fulfillment Summary</div>
              <dl className="mt-4 space-y-2 text-sm">
                <Row label="Orders" value={totals.orders.toString()} />
                <Row label="Items" value={totals.unitCount.toString()} />
                <Row
                  label="Revenue"
                  value={fmt(totals.revenue / 100)}
                />
                <Row
                  label="Shipping"
                  value={fmt(totals.shipping / 100)}
                />
              </dl>
              <div className="mt-3 text-[10px] text-muted-foreground">
                Totals reflect {selectedItems.length ? "selected orders" : "the full cart"}.
              </div>
            </div>

            <div className="panel p-5 space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Bulk actions
              </div>
              <button
                disabled={!selectedItems.length}
                onClick={() => setConfirm("save_tracking")}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-border bg-panel px-3 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-50"
              >
                <Truck className="h-4 w-4" /> Save tracking for selected
              </button>
              <button
                disabled={!selectedItems.length}
                onClick={() => setConfirm("mark_shipped")}
                className="btn-primary w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm disabled:opacity-50"
              >
                <CheckCircle2 className="h-4 w-4" /> Mark as shipped
              </button>
              <div className="pt-2 border-t border-border" />
              <button
                onClick={printSlips}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-border bg-panel px-3 py-2 text-sm font-semibold hover:bg-muted"
              >
                <Printer className="h-4 w-4" /> Print packing slips
              </button>
              <button
                onClick={() => setPickOpen(true)}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-border bg-panel px-3 py-2 text-sm font-semibold hover:bg-muted"
              >
                <ListChecks className="h-4 w-4" /> Show pick list
              </button>
              <button
                disabled
                title="Coming soon"
                className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-border bg-panel px-3 py-2 text-sm font-semibold opacity-50"
              >
                Generate shipping labels
              </button>
            </div>
          </aside>
        </div>
      )}

      {pickOpen && (
        <Modal onClose={() => setPickOpen(false)} title="Pick list">
          <PickList items={selectedItems.length ? selectedItems : items} />
        </Modal>
      )}

      {confirm && (
        <Modal onClose={() => setConfirm(null)} title="Confirm bulk action">
          <p className="text-sm">
            {confirm === "mark_shipped" && (
              <>
                Mark <b>{selectedItems.length}</b> order(s) as <b>shipped</b> and notify the buyer.
              </>
            )}
            {confirm === "save_tracking" && (
              <>
                Save tracking numbers for the selected orders. Orders without both a carrier
                and a tracking number will be skipped, and matching orders will move to{" "}
                <b>shipped</b>.
              </>
            )}
          </p>
          <ul className="mt-3 max-h-40 overflow-y-auto text-xs text-muted-foreground space-y-1">
            {selectedItems.map((i) => (
              <li key={i.transaction.id}>
                #{i.transaction.id.slice(0, 8).toUpperCase()} — {i.order?.card_name ?? "—"}
              </li>
            ))}
          </ul>
          <div className="mt-5 flex justify-end gap-2">
            <button
              onClick={() => setConfirm(null)}
              className="rounded-md border border-border px-3 py-1.5 text-sm font-semibold hover:bg-muted"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={bulkStatus.isPending || bulkTracking.isPending}
              className="btn-primary px-4 py-1.5 text-sm disabled:opacity-50"
            >
              Confirm
            </button>
          </div>
        </Modal>
      )}
    </AppShell>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-mono font-semibold">{value}</dd>
    </div>
  );
}

function Addr({ addr }: { addr: any }) {
  if (!addr || typeof addr !== "object")
    return <div className="text-xs text-muted-foreground">No address on file</div>;
  return (
    <div className="text-xs text-muted-foreground">
      {addr.line1 && <div>{addr.line1}</div>}
      {addr.line2 && <div>{addr.line2}</div>}
      {(addr.city || addr.postal_code) && (
        <div>
          {[addr.city, addr.region, addr.postal_code].filter(Boolean).join(", ")}
        </div>
      )}
      {addr.country && <div>{addr.country}</div>}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const colors: Record<string, string> = {
    funds_held: "border-warning/40 bg-warning/15 text-warning",
    shipped: "border-primary/40 bg-primary/15 text-primary",
    delivered: "border-success/40 bg-success/15 text-success",
    released: "border-success/40 bg-success/15 text-success",
  };
  return (
    <span
      className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
        colors[status] ?? "border-border bg-muted text-muted-foreground"
      }`}
    >
      {status.replace("_", " ")}
    </span>
  );
}

function PickList({ items }: { items: CartItemView[] }) {
  const list = buildPickList(items);
  if (!list.length) return <p className="text-sm text-muted-foreground">Nothing to pick.</p>;
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
        <tr>
          <th className="py-2">Item</th>
          <th className="py-2">TCG</th>
          <th className="py-2 text-right">Qty</th>
        </tr>
      </thead>
      <tbody>
        {list.map((row) => (
          <tr key={row.name} className="border-t border-border">
            <td className="py-2 font-semibold">{row.name}</td>
            <td className="py-2 text-muted-foreground">{row.tcg ?? "—"}</td>
            <td className="py-2 text-right font-mono font-bold">×{row.qty}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Modal({
  children,
  onClose,
  title,
}: {
  children: React.ReactNode;
  onClose: () => void;
  title: string;
}) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="panel w-full max-w-lg p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="rounded-md border border-border p-1 hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

// keep import to silence unused warnings for icons referenced only in JSX above
void Trash2;
